import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Badge, Button, Card, Col, Form, Row, Spinner,
} from 'react-bootstrap';
// @ts-ignore: no def file
import BootstrapTable from 'react-bootstrap-table-next';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';
import { ReactComponent as CopyIcon } from '../../../../assets/images/copy.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/images/delete_t.svg';
import { PageLayout } from '../../../../dorian-shared/types/pageLayout/PageLayout';
import {
  Promotion,
  PromotionListResponse,
  PromotionStatus,
  PromotionStatusFilter,
  PromotionSubcategory,
} from '../types/PromotionTypes';
import { api } from '../../../api';
import { ToolComponentProps } from '../../ToolPage/toolComponentProps';
import { exitToolView } from '../../../utils/toolNavigationUtils';
import { ConfirmModal } from '../../../ui/ConfirmModal/ConfirmModal';
import { PageWrapper } from '../../../ui/PageWrapper';
import { showToast } from '../../../ui/utils';
import {
  PROMOTION_PAGE_SIZES,
  PROMOTION_STATUS_OPTIONS,
  canEditPromotions,
  getSubcategoryTitle,
} from '../utils/promotionUtils';
import { PromotionStatusBadge } from '../components/PromotionStatusBadge/PromotionStatusBadge';

type SortField = 'title' | 'identifier' | 'startTime' | 'endTime';
type SortOrder = 'asc' | 'desc';

interface TableSortState {
  dataField: SortField;
  order: SortOrder;
}

const DEFAULT_SORT: TableSortState = { dataField: 'startTime', order: 'desc' };

function isSameSort(a: TableSortState, b: TableSortState) {
  return a.dataField === b.dataField && a.order === b.order;
}

const dateFormatter = (value: string | null) => (
  value ? <Moment format="YYYY-MM-DD hh:mm A">{value}</Moment> : '—'
);

function buildColumns(
  baseUrl: string,
  subcategories: PromotionSubcategory[],
  isEditor: boolean,
  onCopy: (id: number) => void,
  onArchive: (id: number) => void,
  copyingId: number | null,
) {
  const baseColumns = [
    {
      dataField: 'title',
      text: 'Title',
      sort: true,
      headerStyle: { width: '24%' },
      style: { width: '24%' },
      formatter: (value: string, row: Promotion) => (
        <Link to={`${baseUrl}/${row.id}`}>{value}</Link>
      ),
    },
    {
      dataField: 'identifier',
      text: 'Identifier',
      sort: true,
      headerStyle: { width: '20%' },
      style: { width: '20%' },
    },
    {
      dataField: 'status',
      text: 'Status',
      headerStyle: { width: '9%' },
      style: { width: '9%' },
      formatter: (value: PromotionStatus) => (
        <PromotionStatusBadge status={value} />
      ),
    },
    {
      dataField: 'startTime',
      text: 'Start time',
      sort: true,
      formatter: dateFormatter,
    },
    {
      dataField: 'endTime',
      text: 'End time',
      sort: true,
      formatter: dateFormatter,
    },
    {
      dataField: 'tags',
      text: 'Promo types',
      headerStyle: { width: '14%' },
      style: { width: '14%' },
      formatter: (tags: string[]) => (
        tags.length === 0 ? '—' : tags.map((tag) => (
          <Badge key={tag} variant="secondary" className="mr-1">
            {getSubcategoryTitle(subcategories, tag)}
          </Badge>
        ))
      ),
    },
  ];

  if (!isEditor) {
    return baseColumns;
  }

  return [
    ...baseColumns,
    {
      dataField: 'actions',
      isDummyField: true,
      text: 'Actions',
      headerStyle: { width: '120px' },
      style: { width: '120px' },
      formatter: (_value: unknown, row: Promotion) => (
        <div className="d-flex">
          <Button
            variant="secondary"
            size="sm"
            className="mx-1"
            title="Copy"
            disabled={copyingId === row.id}
            onClick={() => onCopy(row.id)}
          >
            {copyingId === row.id ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <CopyIcon className="btnImg" />
            )}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            className="mx-1"
            title="Delete"
            onClick={() => onArchive(row.id)}
          >
            <DeleteIcon className="btnImg" />
          </Button>
        </div>
      ),
    },
  ];
}

export function PromotionListPage(props: ToolComponentProps) {
  const {
    auth, history, match, toolRole,
  } = props;
  const user = auth.getUser();
  const isEditor = canEditPromotions(toolRole);

  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [subcategories, setSubcategories] = useState<PromotionSubcategory[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PromotionStatusFilter>('');
  const [tagFilter, setTagFilter] = useState('');
  const [tableSort, setTableSort] = useState<TableSortState>(DEFAULT_SORT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyingId, setCopyingId] = useState<number | null>(null);
  const [archiveTargetId, setArchiveTargetId] = useState<number | null>(null);
  const [archiving, setArchiving] = useState(false);
  const loadRequestId = useRef(0);

  const pageLayout: PageLayout = {
    header: { title: 'Promotions' },
    sidebar: {
      nav: [
        {
          title: 'Back',
          action: () => exitToolView(history),
          variant: 'secondary',
          disabled: false,
        },
        ...(isEditor ? [{
          title: 'New promotion',
          action: () => history.push(`${match.url}/new`),
          variant: 'primary' as const,
          disabled: false,
        }] : []),
      ],
    },
  };

  const loadPromotions = useCallback(() => {
    const requestId = loadRequestId.current + 1;
    loadRequestId.current = requestId;
    setLoading(true);
    setError(null);

    api.get<PromotionListResponse>('/v1/tools/promotions', {
      params: {
        page: pageNumber,
        limit: pageSize,
        search: search || undefined,
        status: statusFilter || undefined,
        tag: tagFilter || undefined,
        sortField: tableSort.dataField,
        sortOrder: tableSort.order,
      },
    })
      .then((response) => {
        if (loadRequestId.current !== requestId) {
          return;
        }
        setPromotions(response.data.promotions);
        setSubcategories(response.data.subcategories);
        setTotal(response.data.pagination.total);
      })
      .catch((err) => {
        if (loadRequestId.current !== requestId) {
          return;
        }
        setError(err?.response?.data?.error || 'Failed to load promotions');
      })
      .finally(() => {
        if (loadRequestId.current !== requestId) {
          return;
        }
        setLoading(false);
      });
  }, [pageNumber, pageSize, search, statusFilter, tableSort, tagFilter]);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setPageNumber(1);
    setSearch(searchInput.trim());
  };

  const handleTableChange = useCallback((
    type: string,
    { sortField, sortOrder }: { sortField?: string; sortOrder?: string },
  ) => {
    if (type !== 'sort' || !sortField) {
      return;
    }
    const nextSort: TableSortState = {
      dataField: (['title', 'identifier', 'startTime', 'endTime'].includes(sortField)
        ? sortField
        : 'startTime') as SortField,
      order: sortOrder === 'asc' ? 'asc' : 'desc',
    };
    setTableSort((current) => {
      if (isSameSort(current, nextSort)) {
        return current;
      }
      setPageNumber(1);
      return nextSort;
    });
  }, []);

  const handleCopy = useCallback((id: number) => {
    setCopyingId(id);
    api.post(`/v1/tools/promotions/${id}/copy`)
      .then((response) => {
        showToast({ textMessage: 'Promotion copied', variant: 'success' });
        history.push(`${match.url}/${response.data.promotion.id}`);
      })
      .catch((err) => {
        showToast({
          textMessage: err?.response?.data?.error || 'Failed to copy promotion',
          variant: 'danger',
        });
      })
      .finally(() => setCopyingId(null));
  }, [history, match.url]);

  const handleArchiveConfirm = () => {
    if (!archiveTargetId) {
      return;
    }
    setArchiving(true);
    api.post(`/v1/tools/promotions/${archiveTargetId}/archive`)
      .then(() => {
        showToast({ textMessage: 'Promotion deleted', variant: 'success' });
        setArchiveTargetId(null);
        loadPromotions();
      })
      .catch((err) => {
        showToast({
          textMessage: err?.response?.data?.error || 'Failed to delete promotion',
          variant: 'danger',
        });
      })
      .finally(() => setArchiving(false));
  };

  const columns = useMemo(
    () => buildColumns(match.url, subcategories, isEditor, handleCopy, setArchiveTargetId, copyingId),
    [copyingId, handleCopy, isEditor, match.url, subcategories],
  );

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  return (
    <PageWrapper user={user} page={pageLayout}>
      <Card>
        <Card.Body>
          <Card.Title>Promotions</Card.Title>

          <Form onSubmit={handleSearch} className="mb-3">
            <Row>
              <Col md={5}>
                <Form.Control
                  placeholder="Search title or identifier"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Control
                  as="select"
                  value={statusFilter}
                  onChange={(event) => {
                    setStatusFilter(event.target.value as PromotionStatusFilter);
                    setPageNumber(1);
                  }}
                >
                  {PROMOTION_STATUS_OPTIONS.map((option) => (
                    <option key={option.label} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Control>
              </Col>
              <Col md={3}>
                <Form.Control
                  as="select"
                  value={tagFilter}
                  onChange={(event) => {
                    setTagFilter(event.target.value);
                    setPageNumber(1);
                  }}
                >
                  <option value="">All promo types</option>
                  {subcategories.map((subcategory) => (
                    <option key={subcategory.key} value={subcategory.key}>
                      {getSubcategoryTitle(subcategories, subcategory.key)}
                    </option>
                  ))}
                </Form.Control>
              </Col>
              <Col md={1}>
                <Button type="submit" variant="secondary" className="w-100">Search</Button>
              </Col>
            </Row>
          </Form>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading && promotions.length === 0 ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <BootstrapTable
                keyField="id"
                data={promotions}
                columns={columns}
                bootstrap4
                striped
                hover
                remote={{ sort: true }}
                onTableChange={handleTableChange}
                sort={tableSort}
                noDataIndication="No promotions found"
              />
              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
                <div className="d-flex align-items-center mb-2 mb-md-0">
                  <span className="mr-3">
                    Page
                    {' '}
                    {pageNumber}
                    {' '}
                    of
                    {' '}
                    {totalPages}
                    {' '}
                    (
                    {total}
                    {' '}
                    promotions)
                    {loading && (
                      <>
                        {' '}
                        <Spinner animation="border" size="sm" />
                      </>
                    )}
                  </span>
                  <Form.Control
                    as="select"
                    size="sm"
                    className="w-auto"
                    value={pageSize}
                    onChange={(event) => {
                      setPageSize(Number(event.target.value));
                      setPageNumber(1);
                    }}
                  >
                    {PROMOTION_PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>
                        {size}
                        {' '}
                        per page
                      </option>
                    ))}
                  </Form.Control>
                </div>
                <div>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mr-2"
                    disabled={pageNumber <= 1}
                    onClick={() => setPageNumber((current) => Math.max(current - 1, 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={pageNumber >= totalPages}
                    onClick={() => setPageNumber((current) => Math.min(current + 1, totalPages))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <ConfirmModal
        show={archiveTargetId !== null}
        onHide={() => setArchiveTargetId(null)}
        title="Delete Promotion"
        confirmButtonTitle="Delete"
        onConfirm={handleArchiveConfirm}
        onCancel={() => setArchiveTargetId(null)}
        isLoading={archiving}
      >
        Delete this promotion? This cannot be undone.
      </ConfirmModal>
    </PageWrapper>
  );
}
